'use client'

import React, { memo } from 'react'
import { IBanner } from '@/app/[locale]/_types'
import { AspectRatio, Box, useBreakpointValue } from '@chakra-ui/react'
import Image from 'next/image'
import Link from 'next/link'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import Mobile from './responsive/mobile'
import Desktop from './responsive/desktop'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'

const isExternalUrl = (url: string | undefined): boolean => {
  if (!url || typeof window === 'undefined') return false
  return !url.includes(window.location.hostname)
}

const getFullUrl = (
  url: string | undefined,
  baseUrl: string | undefined
): string => {
  if (!url || !baseUrl) return ''
  return url.includes(baseUrl) ? url : `${baseUrl}${url}`
}

const aspectRatio = 3 / 1

const Banner = ({ list }: { list: IBanner[] }) => {
  const currentLocale = useCustomLocale()
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )

  const slides = React.useMemo(
    () =>
      list
        ?.filter((item) => item?.image?.[currentLocale])
        .map((item, index) => {
          const imageUrl = getFullUrl(
            item?.image?.[currentLocale],
            process.env.BASE_URL
          )

          const videoUrl =
            item?.media_type === 'video'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_VIDEO
                )
              : null

          const gifUrl =
            item?.media_type === 'gif'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_GIF
                )
              : null

          const bannerContent = (
            <AspectRatio
              display='grid'
              placeItems='center'
              position='relative'
              ratio={aspectRatio}
              maxH={{ base: '220px', lg: '440px' }}
              borderRadius={{ base: '16px', lg: '64px' }}
              overflow='hidden'
              maxW={'1440px'}
              mx={'auto'}
            >
              {item?.media_type === 'video' && videoUrl ? (
                <video
                  style={{
                    width: '100%',
                    aspectRatio: `${aspectRatio}`,
                    objectFit: 'cover',
                  }}
                  autoPlay
                  muted
                  loop
                  preload='metadata'
                  playsInline
                >
                  <source src={videoUrl} />
                </video>
              ) : item?.media_type === 'gif' && gifUrl ? (
                <Image
                  src={gifUrl}
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  style={{
                    objectFit: 'cover',
                    borderRadius: '16px',
                  }}
                  priority={true}
                  fill
                />
              ) : imageUrl ? (
                <Image
                  src={imageUrl}
                  priority={true}
                  style={{
                    objectFit: 'cover',
                    borderRadius: '16px',
                  }}
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  fetchPriority='high'
                  fill
                />
              ) : (
                <></>
              )}
            </AspectRatio>
          )

          const slideContent = (
            <Box
              className='embla__slide'
              key={item.id || index}
              pr={list.length - 1 === index ? 2 : 0}
            >
              {item?.url ? (
                <Link
                  href={createUrlWithParams(item.url)}
                  target={isExternalUrl(item.url) ? '_blank' : '_self'}
                  rel='noreferrer'
                >
                  <div>{bannerContent}</div>
                </Link>
              ) : (
                bannerContent
              )}
            </Box>
          )

          return slideContent
        }),
    [list, currentLocale]
  )

  if (!list?.length) return null

  return (
    <div>
      {isDesktop ? <Desktop slides={slides} /> : <Mobile slides={slides} />}
    </div>
  )
}

export default memo(Banner)
